<template>
  <div class="tw:flex tw:flex-col tw:gap-4">
    <SuggestedKeywordsBooleanSection
      v-model="must"
      :ai-feature-configuration-id="aiFeatureConfigurationId"
      data-testid="must"
      :disabled="disabled"
      :error="hasError && showError"
      :error-message="
        getLocaleText('stream_settings_simple_boolean.error_message')
      "
      :is-ai-button-clearable="isAIButtonClearable"
      :is-ai-button-disabled="isAIButtonDisabled"
      :is-container-open="isContainerOpen"
      :is-social-stream="isSocialStream"
      :keyword-api-error="keywordApiError"
      :keyword-suggestions="keywordSuggestions.must"
      :loading-suggestions="loadingSuggestions"
      must
      :safety-error="safetyError"
      @regenerate-suggestions="getSuggestedKeywords"
      @toggle-ai-feature="toggleAIFeature"
    />
    <SuggestedKeywordsBooleanSection
      v-model="should"
      data-testid="should"
      :disabled="disabled"
      :is-ai-button-clearable="isAIButtonClearable"
      :is-container-open="isContainerOpen"
      :is-social-stream="isSocialStream"
      :keyword-api-error="keywordApiError"
      :keyword-suggestions="keywordSuggestions.should"
      :loading-suggestions="loadingSuggestions"
      :safety-error="safetyError"
      should
      @regenerate-suggestions="getSuggestedKeywords"
    />
    <SuggestedKeywordsBooleanSection
      v-model="mustNot"
      data-testid="must-not"
      :disabled="disabled"
      :is-ai-button-clearable="isAIButtonClearable"
      :is-container-open="isContainerOpen"
      :is-social-stream="isSocialStream"
      :keyword-api-error="keywordApiError"
      :keyword-suggestions="keywordSuggestions.mustNot"
      :loading-suggestions="loadingSuggestions"
      must-not
      :safety-error="safetyError"
      @regenerate-suggestions="getSuggestedKeywords"
    />
  </div>
</template>

<script setup lang="ts">
import { type AxiosError } from "axios";
import { computed, onMounted, ref } from "vue";

import { track } from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import type {
  SimpleBoolean,
  SimpleBooleanFilter,
} from "shared/resources/Stream";
import features from "shared/services/features";

import SuggestedKeywordsBooleanSection from "./SuggestedKeywordsBooleanSection.vue";

interface SuggestedKeywordsBooleanProps {
  showError?: boolean;
  disabled?: boolean;
  isSocialStream?: boolean;
}

const props = defineProps<SuggestedKeywordsBooleanProps>();

const simpleBoolean = defineModel<SimpleBoolean>("simpleBoolean");

const aiFeatureConfigurationId = ref<number>();
const isContainerOpen = ref(false);
const loadingSuggestions = ref(false);
const keywordApiError = ref(false);
const safetyError = ref(false);

const keywordSuggestionsValue: Record<string, string[]> = {
  must: [],
  should: [],
  mustNot: [],
};

const keywordSuggestions = ref<Record<string, string[]>>({
  ...keywordSuggestionsValue,
});

const must = computed<SimpleBooleanFilter[]>({
  get() {
    return simpleBoolean.value?.must || [];
  },
  set(value) {
    simpleBoolean.value = {
      ...simpleBoolean.value,
      must: value,
    };
  },
});

const should = computed<SimpleBooleanFilter[]>({
  get() {
    return simpleBoolean.value?.should || [];
  },
  set(value) {
    simpleBoolean.value = {
      must: [],
      ...simpleBoolean.value,
      should: value,
    };
  },
});

const mustNot = computed<SimpleBooleanFilter[]>({
  get() {
    return simpleBoolean.value?.must_not || [];
  },
  set(value) {
    simpleBoolean.value = {
      must: [],
      ...simpleBoolean.value,
      must_not: value,
    };
  },
});

const hasError = computed(() => !must.value.length);

const isAIButtonClearable = computed(() =>
  Boolean(keywordSuggestions.value.must.length)
);

const isAIButtonDisabled = computed(() => !must.value.length);

function parseSimpleBoolean() {
  if (!simpleBoolean.value) return;

  must.value = simpleBoolean.value.must || [];
  should.value = simpleBoolean.value.should || [];
  mustNot.value = simpleBoolean.value.must_not || [];
}

function clearSuggestedKeywords() {
  keywordSuggestions.value = { ...keywordSuggestionsValue };
  isContainerOpen.value = false;
  keywordApiError.value = false;
}

function toggleAIFeature() {
  if (isAIButtonClearable.value) {
    clearSuggestedKeywords();
  } else {
    getSuggestedKeywords();
  }
}

function clearSuggestionIndex(filters: SimpleBooleanFilter[]) {
  filters
    .filter((filter) => filter.isSuggestion)
    .forEach((filter) => {
      Object.assign(filter, { suggestionIndex: null });
    });
}

function convertToNormalFilters() {
  clearSuggestionIndex(must.value);
  clearSuggestionIndex(should.value);
  clearSuggestionIndex(mustNot.value);
}

interface KeywordSuggestionResponse {
  aiFeatureConfigurationId: number;
  inclusions: {
    or: string[];
    and: string[];
  };
  exclusions?: string[];
}

async function getSuggestedKeywords() {
  if (
    !features.has("has_ai_keyword_suggestion") ||
    isAIButtonDisabled.value ||
    loadingSuggestions.value
  )
    return;

  isContainerOpen.value = true;
  keywordApiError.value = false;

  try {
    convertToNormalFilters();

    track(
      `Clicked AI button in advanced keywords - ${
        props.isSocialStream ? "Social" : "Monitoring"
      }`
    );

    loadingSuggestions.value = true;
    keywordSuggestions.value = { ...keywordSuggestionsValue };

    const { data } = await $streemApiV1.post<KeywordSuggestionResponse>(
      "ai/services/suggested_keywords",
      {
        params: {
          keywords: must.value
            .concat(should.value)
            .map((filter) => filter.keyword),
          keywordType: "simple_boolean",
        },
        transformKeys: true,
      }
    );

    aiFeatureConfigurationId.value = data.aiFeatureConfigurationId;

    keywordSuggestions.value = {
      must: data.inclusions.and,
      should: data.inclusions.or,
      mustNot: data.exclusions ?? [],
    };
  } catch (error) {
    keywordApiError.value = true;

    const err = error as AxiosError;

    if (err.response?.status === 451) {
      safetyError.value = true;
    }
  } finally {
    loadingSuggestions.value = false;
  }
}

onMounted(() => {
  parseSimpleBoolean();
});
</script>
